import { Typography } from "@material-ui/core"
import { FinishingLevelEngine } from "api"
import { IEMediaQuery } from "common/MediaQueries"
import { formatPrice } from "common/price"
import { useEffect, useMemo, useState } from "react"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import { Font } from "../wizard/constants"
import { setSelectedDetailing } from "./detailingSlice"

const HORIZONTAL_SPACING = 3

const StyledHeading = styled(Typography).attrs({
  variant: "h2",
  component: "h1",
  tabIndex: 0,
})`
  font-size: 16px;
  margin-bottom: 8px;
`
const StyledSubtitle = styled(Typography).attrs({
  variant: "h2",
  component: "h2",
})`
  font-size: 16px;
  margin-bottom: 8px;
`
const StyledDetailings = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 18px;
`
const StyledEngineName = styled.span`
  font-family: ${Font.OENaPro};
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.05em;
  text-align: center;
`
const StyledPrice = styled.span`
  font-size: 10px;
  font-weight: 400;
  line-height: 10px;
  text-align: center;
`

const StyledDetailing = styled.div`
  flex-basis: calc(100% / 3 - ${HORIZONTAL_SPACING}px * 2);
  flex-shrink: 0;
  margin: 3px ${HORIZONTAL_SPACING}px;
  min-height: 66px;
`

const StyledEngine = styled.div`
  flex-basis: calc(100% / 2 - ${HORIZONTAL_SPACING}px * 2);
  flex-shrink: 0;
  margin: 3px ${HORIZONTAL_SPACING}px;
`
const StyledRadio = styled.input`
  position: absolute;
  border: none;
  margin: -1px;
  padding: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  &:focus-visible + label {
    border: 2px dashed white;
    outline: 3px dashed #0e3487;
    outline-offset: 2px;
  }
  clip: rect(0px, 0px, 0px, 0px);
  :checked + label {
    border: none;
    background-color: ${(p) => p.theme.colors.black};
    color: ${(p) => p.theme.colors.white};
    > div {
      color: ${(p) => p.theme.colors.white};
    }
  }
`
const StyledLabel = styled.label`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${(p) => p.theme.shape.borderRadius}px;
  cursor: pointer;
  background: ${(p) => p.theme.colors.white};
  line-height: 19px;
  padding: 15px 0px;
  height: 100%;

  ${IEMediaQuery} {
    flex-basis: auto;
    width: calc(100% / 3 - ${HORIZONTAL_SPACING}px * 2 - 1px);
  }
`

const StyledEngineLabel = styled(StyledLabel)``

const StyledDetailLabel = styled(StyledLabel)`
  padding: 6px 0;
`

const StyledDetailName = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: center;
`

const StyledOutOfStock = styled.div`
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: ${(p) => p.theme.colors.mazdaRed};
  text-align: center;
  margin-top: 4px;
`

const DetailingButtons = () => {
  const { engines, selectedDetailingCode } = useTypedSelector(
    (state) => state.detailing
  )
  const { showOnlyStock } = useTypedSelector((state) => state.wizard)

  const [selectedEngine, setSelectedEngine] =
    useState<FinishingLevelEngine>(null)

  const dispatch = useAppDispatch()

  // Run this effect when the engines change
  useEffect(() => {
    if (!selectedDetailingCode) return
    // Find the engine with the finishing level that matches the selected detailing
    const engine = engines.find((engine) =>
      engine.finishingLevels.some(
        (detailing) => detailing.details.code === selectedDetailingCode
      )
    )
    // Set selected engine to the engine we found
    setSelectedEngine(engine)
  }, [engines, selectedDetailingCode])

  const enginesToDisplay = useMemo(() => {
    return engines?.filter((engine) => {
      if (!showOnlyStock) return true
      // Filter engines that have at least one finishing level in stock
      return engine.finishingLevels.some(
        (detailing) => detailing.details.inStock
      )
    })
  }, [engines, showOnlyStock])

  useEffect(() => {
    if (!selectedEngine) return
    if (showOnlyStock) {
      const selectedDetailing = selectedEngine.finishingLevels.find(
        (detailing) => detailing.details.code === selectedDetailingCode
      )

      if (
        !enginesToDisplay?.some((e) => e.engineId === selectedEngine.engineId)
      ) {
        // If the selected engine is not in the filtered list, reset to the first available engine
        setSelectedEngine(enginesToDisplay[0])
      }
      // If the selected detailing is not in stock, try to select the first in-stock detailing
      if (!selectedDetailing?.details.inStock) {
        const firstInStock = selectedEngine.finishingLevels.find(
          (detailing) => detailing.details.inStock
        )
        if (
          firstInStock &&
          firstInStock.details.code !== selectedDetailingCode
        ) {
          dispatch(setSelectedDetailing(firstInStock.details.code))
        }
      }
      // If the selected detailing is in stock, do nothing (don't reset to first)
    }
  }, [showOnlyStock, selectedEngine, selectedDetailingCode, dispatch])

  const selectedEngineFinishingLevels = useMemo(() => {
    return selectedEngine?.finishingLevels.filter((item) =>
      showOnlyStock ? item.details?.inStock : true
    )
  }, [selectedEngine?.finishingLevels, showOnlyStock])

  return (
    <>
      <StyledHeading id="detailingHeadline">בחירת מנוע</StyledHeading>

      {selectedEngineFinishingLevels?.length > 0 && (
        <StyledDetailings role="radiogroup" aria-labelledby="detailingHeadline">
          {enginesToDisplay?.map((engine) => (
            <StyledEngine key={engine.engineId} role="presentation">
              <StyledRadio
                type="radio"
                name="engine"
                value={engine.engineId}
                id={`radio_engine${engine.engineId}`}
                checked={selectedEngine?.engineId === engine.engineId}
                onChange={() => {
                  if (selectedEngine?.engineId === engine.engineId) return
                  setSelectedEngine(engine)
                }}
              />
              <StyledEngineLabel htmlFor={`radio_engine${engine.engineId}`}>
                <StyledEngineName>מנוע {engine.engineIdName}</StyledEngineName>
              </StyledEngineLabel>
            </StyledEngine>
          ))}
        </StyledDetailings>
      )}

      <StyledSubtitle>בחירת רמת גימור</StyledSubtitle>

      <StyledDetailings role="radiogroup" aria-labelledby="detailingHeadline">
        {selectedEngineFinishingLevels?.map(({ details, price }) => (
          <StyledDetailing key={details.code} role="presentation">
            <StyledRadio
              type="radio"
              name="detailing"
              value={details.code}
              id={`radio_${details.code}`}
              checked={details.code === selectedDetailingCode}
              onChange={() => {
                if (details.code === selectedDetailingCode) return
                dispatch(setSelectedDetailing(details.code))
              }}
            />
            <StyledDetailLabel htmlFor={`radio_${details.code}`}>
              <StyledDetailName>{details.name}</StyledDetailName>
              <StyledPrice>{formatPrice(price.originalPrice)}</StyledPrice>
              {!details.inStock && (
                <StyledOutOfStock>לא במלאי</StyledOutOfStock>
              )}
            </StyledDetailLabel>
          </StyledDetailing>
        ))}
      </StyledDetailings>
    </>
  )
}

export default DetailingButtons
